import { ApiResponse, ClaimStatus, OmitFirstArg, TaskTxStatus, TaskType } from '../types';
import ky, { KyInstance, Options } from 'ky';

export type Task = {
  taskUuid: string;
  taskKey: string;
  name: string;
  desc: string;
  type: TaskType;
  picUrl: string;
  rewardPoints: number;
  jumpUrl: string;
  isDone: boolean;
  isClaimed: ClaimStatus;
  claimAt: number;
  txHash: string;
  txStatus: TaskTxStatus;
};

export type TaskApiResult = {
  total: number;
  totalRewardPoints: number;
  tasks: Task[];
};

/**
 * get tasks
 * [need auth]
 * @param api - The KyInstance
 * @param options - Additional request options
 * @returns Promise resolving to get tasks result
 * @example
 * ```typescript
 * const result = await getTasks(api);
 * ```
 */
export const getTasks = async (api: KyInstance, options?: Options): Promise<ApiResponse<TaskApiResult>> => {
  return await api.get<ApiResponse<TaskApiResult>>('get-tasks', options).json();
};

export type ShareTaskBody = {
  taskKey: string;
};

export type ShareTaskResult = {
  txHash: string;
};

/**
 * share task done
 * [need auth]
 * @param api - The KyInstance
 * @param json - The request body
 * @param options - Additional request options
 * @returns Promise resolving to share task done result
 * @example
 * ```typescript
 * const result = await shareTaskDone(api, { taskKey: 'example' });
 * ```
 */
export const shareTaskDone = async (api: KyInstance, json: ShareTaskBody, options?: Options) => {
  return await api.post<ApiResponse<ShareTaskResult>>('share-task-done', { json, ...options }).json();
};

export type ClaimTaskBody = {
  taskKey: string;
};

export type ClaimTaskResult = {
  txHash: string;
};

/**
 * claim task reward
 * [need auth]
 * @param api - The KyInstance
 * @param json - The request body
 * @param options - Additional request options
 * @returns Promise resolving to claim task reward result
 * @example
 * ```typescript
 * const result = await claimTaskReward(api, { taskKey: 'example' });
 * ```
 */
export const claimTaskReward = async (api: KyInstance, json: ClaimTaskBody, options?: Options) => {
  return await api.post<ApiResponse<ClaimTaskResult>>('claim-task-reward', { json, ...options }).json();
};

export type TaskApi = {
  getTasks: OmitFirstArg<typeof getTasks>;
  shareTaskDone: OmitFirstArg<typeof shareTaskDone>;
  claimTaskReward: OmitFirstArg<typeof claimTaskReward>;
};

export const createTaskApi = (initOptions: Options): TaskApi => {
  const mergedOptions: Options = { ...initOptions, prefixUrl: `${initOptions.prefixUrl}/task` };
  const taskApi = ky.extend(mergedOptions);
  return {
    getTasks: options => getTasks(taskApi, options),
    shareTaskDone: (json, options) => shareTaskDone(taskApi, json, options),
    claimTaskReward: (json, options) => claimTaskReward(taskApi, json, options),
  };
};

export default createTaskApi;
